
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Settings, User, Award, Calendar, Gift, Shield, LogOut, MessageSquare, FileBadge, Users } from 'lucide-react';
import LanguageSwitcher from '@/components/ui-elements/language-switcher';

interface AdminHeaderMobileMenuProps {
  isOpen: boolean;
  handleGoToDashboard: () => void;
  handleOpenDialog: () => void;
  handleOpenAppSettings: () => void;
  handleOpenEventsManagement: () => void;
  handleOpenBadgesManagement: () => void;
  handleLogout: () => void;
}

const AdminHeaderMobileMenu: React.FC<AdminHeaderMobileMenuProps> = ({ 
  isOpen, 
  handleGoToDashboard, 
  handleOpenDialog, 
  handleOpenAppSettings, 
  handleOpenEventsManagement, 
  handleOpenBadgesManagement, 
  handleLogout 
}) => {
  const { t } = useTranslation(['admin', 'dashboard', 'common', 'navigation', 'language']);
  
  if (!isOpen) return null;
  
  const menuVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3
      }
    })
  };
  
  return (
    <motion.div 
      className="absolute top-full left-0 right-0 bg-[#1A1F2C] border-t border-gray-700 py-2 z-10"
      variants={menuVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      aria-label={t('admin:mobileMenu')}
    >
      <div className="space-y-1 px-4">
        <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible">
          <Link 
            to="/admin"
            className="w-full p-3 text-left text-white hover:bg-white/10 rounded-md flex items-center"
          >
            <Users className="h-5 w-5 mr-3" />
            <span>{t('admin:users')}</span>
          </Link>
        </motion.div>
        
        <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible">
          <Link 
            to="/admin/applications"
            className="w-full p-3 text-left text-white hover:bg-white/10 rounded-md flex items-center"
          >
            <FileBadge className="h-5 w-5 mr-3" />
            <span>{t('admin:applications')}</span>
          </Link>
        </motion.div>
        
        <motion.div custom={2} variants={itemVariants} initial="hidden" animate="visible">
          <Link 
            to="/admin/quick-interest"
            className="w-full p-3 text-left text-white hover:bg-white/10 rounded-md flex items-center"
          >
            <MessageSquare className="h-5 w-5 mr-3" />
            <span>{t('navigation:partner')}</span>
          </Link>
        </motion.div>
        
        <motion.div custom={3} variants={itemVariants} initial="hidden" animate="visible">
          <Link 
            to="/admin/events"
            className="w-full p-3 text-left text-white hover:bg-white/10 rounded-md flex items-center"
          >
            <Calendar className="h-5 w-5 mr-3" />
            <span>{t('admin:events')}</span>
          </Link>
        </motion.div>
        
        <motion.div custom={4} variants={itemVariants} initial="hidden" animate="visible">
          <Link 
            to="/admin/badges"
            className="w-full p-3 text-left text-white hover:bg-white/10 rounded-md flex items-center"
          >
            <Award className="h-5 w-5 mr-3" />
            <span>{t('admin:badges')}</span>
          </Link>
        </motion.div>
        
        <motion.div custom={5} variants={itemVariants} initial="hidden" animate="visible">
          <Link 
            to="/admin/quests"
            className="w-full p-3 text-left text-white hover:bg-white/10 rounded-md flex items-center"
          >
            <Gift className="h-5 w-5 mr-3" />
            <span>{t('admin:quests')}</span>
          </Link>
        </motion.div>
        
        <motion.div custom={6} variants={itemVariants} initial="hidden" animate="visible">
          <button 
            className="w-full p-3 text-left text-white hover:bg-white/10 rounded-md flex items-center"
            onClick={handleOpenDialog}
          >
            <Shield className="h-5 w-5 mr-3" />
            <span>{t('admin:makeAdmin')}</span>
          </button>
        </motion.div>
        
        <motion.div custom={7} variants={itemVariants} initial="hidden" animate="visible">
          <Link 
            to="/admin/settings"
            className="w-full p-3 text-left text-white hover:bg-white/10 rounded-md flex items-center"
          >
            <Settings className="h-5 w-5 mr-3" />
            <span>{t('admin:settings')}</span>
          </Link>
        </motion.div>
        
        <motion.div custom={8} variants={itemVariants} initial="hidden" animate="visible">
          <div className="w-full p-3 flex justify-center">
            <LanguageSwitcher variant="outline" className="w-full" isMobile={true} aria-label={t('language:changeLanguage')} />
          </div>
        </motion.div>
        
        <motion.div custom={9} variants={itemVariants} initial="hidden" animate="visible">
          <button 
            className="w-full p-3 text-left text-white hover:bg-white/10 rounded-md flex items-center"
            onClick={handleGoToDashboard}
          >
            <User className="h-5 w-5 mr-3" />
            <span>{t('navigation:userDashboard')}</span>
          </button>
        </motion.div>
        
        <motion.div custom={10} variants={itemVariants} initial="hidden" animate="visible">
          <button 
            className="w-full p-3 text-left text-white hover:bg-white/10 rounded-md flex items-center text-red-400"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5 mr-3" />
            <span>{t('dashboard:signOut')}</span>
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AdminHeaderMobileMenu;
